import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { toast } from "react-hot-toast";
import { FaArrowLeft, FaCheck, FaTimes, FaSpinner } from "react-icons/fa";
import axiosInstance from "../utils/axiosConfig";
import Loader from "../components/Loader";

const DepartmentReviewScreen = () => {
  const { paperId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPaperDetails();
  }, [paperId]);

  const fetchPaperDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/department/papers/${paperId}`);
      setPaper(response.data);
    } catch (error) {
      console.error("Error fetching paper details:", error);
      toast.error(error.response?.data?.message || "Failed to load paper details");
      setError(error.response?.data?.message || "Failed to load paper details");
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmit = async (status) => {
    if (submitting) return;

    try {
      setSubmitting(true);
      const response = await axiosInstance.post(`/api/department/papers/${paperId}/review`, {
        status,
        comment: reviewComment
      });
      
      toast.success(response.data.message || `Paper ${status} successfully`);
      navigate('/department/dashboard');
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error(error.response?.data?.message || `Failed to ${status} paper`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen pt-24 pb-12 flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <Loader size="large" text="Loading paper details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen pt-24 pb-12 flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={() => navigate('/department/dashboard')}
          className={`px-4 py-2 rounded-md flex items-center ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'}`}
        >
          <FaArrowLeft className="mr-2" /> Back to Dashboard
        </button>
      </div>
    );
  }

  if (!paper) {
    return (
      <div className={`min-h-screen pt-24 pb-12 flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-lg mb-4">Paper not found</div>
        <button
          onClick={() => navigate('/department/dashboard')}
          className={`px-4 py-2 rounded-md flex items-center ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'}`}
        >
          <FaArrowLeft className="mr-2" /> Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-24 pb-12 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate('/department/dashboard')}
            className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'}`}
          >
            <FaArrowLeft />
          </button>
          <h1 className="text-2xl font-bold">Review Paper</h1>
        </div>

        <div className={`rounded-xl p-6 mb-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="mb-6">
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold">{paper.title}</h2>
                <div className="mt-2 text-sm">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Submitted by: </span>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{paper.faculty?.name}</span>
                </div>
                <div className="mt-1 text-sm">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Subject: </span>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{paper.subject?.name}</span>
                </div>
              </div>
              <div className="flex gap-4">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDarkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-100 text-indigo-700'
                }`}>
                  {paper.questions.length} Questions
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  Pending Review
                </div>
              </div>
            </div>
            {paper.description && (
              <p className={`mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {paper.description}
              </p>
            )}
          </div>

          <div className="my-8">
            <h3 className="text-xl font-semibold mb-4">Questions</h3>
            <div className="space-y-6">
              {paper.questions.map((question, index) => (
                <div 
                  key={question._id || index}
                  className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Question #{index + 1}
                    </div>
                  </div>
                  <p className="mb-4">{question.text}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {question.options?.map((option, optIndex) => (
                      <div 
                        key={optIndex}
                        className={`p-3 rounded-md border flex items-start ${
                          option.isCorrect
                            ? isDarkMode 
                              ? 'bg-green-900/20 border-green-800 text-green-400' 
                              : 'bg-green-50 border-green-200 text-green-800'
                            : isDarkMode
                              ? 'bg-gray-800 border-gray-700'
                              : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className={`w-6 h-6 flex items-center justify-center rounded-full mr-2 ${
                          option.isCorrect
                            ? isDarkMode ? 'bg-green-900 text-green-400' : 'bg-green-100 text-green-800'
                            : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {['A', 'B', 'C', 'D', 'E', 'F'][optIndex]}
                        </div>
                        <div className="flex-1">
                          {option.text}
                        </div>
                        {option.isCorrect && (
                          <FaCheck className={isDarkMode ? 'text-green-400' : 'text-green-600'} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-xl font-semibold mb-4">Review</h3>
            <div className="mb-4">
              <label htmlFor="reviewComment" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Feedback / Comments
              </label>
              <textarea
                id="reviewComment"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                rows={4}
                className={`block w-full rounded-md shadow-sm ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white focus:ring-indigo-500 focus:border-indigo-500' 
                    : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                }`}
                placeholder="Provide feedback to the faculty member (optional)"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <button
                onClick={() => handleReviewSubmit('rejected')}
                disabled={submitting}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2"
              >
                {submitting ? <Loader size="small" /> : <FaTimes />}
                Reject Paper
              </button>
              <button
                onClick={() => handleReviewSubmit('approved')}
                disabled={submitting}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors inline-flex items-center justify-center gap-2"
              >
                {submitting ? <Loader size="small" /> : <FaCheck />}
                Approve Paper
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentReviewScreen; 